import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { 
  History, Search, Filter, Download, Calendar, Package, 
  DollarSign, Heart, Clock, Loader2, FileText
} from 'lucide-react';
import { doadorApiCall } from './DoadorPortalLayout';
import { toast } from 'sonner';

const TIPOS_DOACAO = ['Produto', 'Financeira', 'Voluntariado', 'Equipamento', 'Serviço'];
const STATUS_DOACAO = ['Pendente', 'Aprovada', 'Concluída', 'Entregue', 'Cancelada'];

export default function DoadorDoacoesPage() {
  const [loading, setLoading] = useState(true);
  const [doacoes, setDoacoes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filtros, setFiltros] = useState({ tipo: '', status: '', search: '' });

  useEffect(() => {
    loadDoacoes();
  }, [page, filtros.tipo, filtros.status]);

  const loadDoacoes = async () => {
    try {
      setLoading(true);
      let url = `/doador/doacoes?skip=${page * 20}&limit=20`;
      if (filtros.tipo) url += `&tipo=${filtros.tipo}`;
      if (filtros.status) url += `&status=${filtros.status}`;
      
      const data = await doadorApiCall(url);
      setDoacoes(data.doacoes || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Erro ao carregar doações');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pendente': 'bg-yellow-100 text-yellow-700',
      'Aprovada': 'bg-blue-100 text-blue-700',
      'Concluída': 'bg-green-100 text-green-700',
      'Entregue': 'bg-green-100 text-green-700',
      'Cancelada': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      'Produto': Package,
      'Financeira': DollarSign,
      'Voluntariado': Heart,
      'Equipamento': Package,
      'Serviço': Clock
    };
    const Icon = icons[tipo] || Package;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6" data-testid="doador-doacoes-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <History className="w-7 h-7 text-rose-500" />
          Minhas Doações
        </h1>
        <p className="text-slate-500 mt-1">Histórico completo das suas contribuições</p>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar doações..."
                  className="pl-9"
                  value={filtros.search}
                  onChange={(e) => setFiltros({...filtros, search: e.target.value})}
                />
              </div>
            </div>
            <Select value={filtros.tipo || "all"} onValueChange={(v) => setFiltros({...filtros, tipo: v === "all" ? "" : v})}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo de doação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {TIPOS_DOACAO.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtros.status || "all"} onValueChange={(v) => setFiltros({...filtros, status: v === "all" ? "" : v})}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {STATUS_DOACAO.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
            </div>
          ) : doacoes.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Sem doações registadas</h3>
              <p className="text-slate-500 mt-1">As suas doações aparecerão aqui</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Campanha</TableHead>
                    <TableHead>Valor Est.</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {doacoes.map((doacao) => (
                    <TableRow key={doacao.id}>
                      <TableCell className="font-medium">
                        {doacao.data_doacao ? new Date(doacao.data_doacao).toLocaleDateString('pt-PT') : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTipoIcon(doacao.tipo_doacao)}
                          {doacao.tipo_doacao}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {doacao.descricao || '-'}
                      </TableCell>
                      <TableCell>
                        {doacao.campanha?.nome || '-'}
                      </TableCell>
                      <TableCell>
                        {doacao.valor_estimado ? `${doacao.valor_estimado.toLocaleString('pt-PT')}€` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(doacao.status)}>
                          {doacao.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {doacao.status === 'Concluída' && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={`/doador/certificados/gerar/${doacao.id}`}>
                              <FileText className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Paginação */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Mostrando {doacoes.length} de {total} doações
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Anterior
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={(page + 1) * 20 >= total}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Seguinte
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
